import { IPendingCareMapInvite } from '../caremap/ICareMapInvite';
import { CommunityModelFactory } from './CommunityModelFactory';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { AppPrincipal } from '../auth/AppPrincipal';
import { UserState } from '../user/UserState';
import { CollaboratorRow } from './CollaboratorRow';
import { ICollaboratorPermission, ICollaborators } from './CommunityClient';

interface ICollaboratorRowAdapterProps {
    invite?: IPendingCareMapInvite;
    permission?: ICollaboratorPermission;
    owner?: ICollaborators['owner'];
};

export const CollaboratorRowAdapter: React.FC<ICollaboratorRowAdapterProps> = (props) => {
    const { invite, permission, owner } = props;

    const trackingService = useInstance(MixPanelEventEmitter);

    const communityModelFactory = useInstance(CommunityModelFactory);
    useSubscription(() => communityModelFactory);

    const community = communityModelFactory.forActiveCaremap();

    const userState = useInstance(UserState);
    useSubscription(() => userState);

    const principal = useInstance(AppPrincipal);

    if (invite){
        return (
            <CollaboratorRow
                id={invite.token}
                data-type="invite"
                type="invite"
                email={invite.intendedRecipient.email || ''}
                name={invite.intendedRecipient.name || ''}
                createdAt={invite.createdAt}
                onDelete={async () => {
                    trackingService.track('Invitation Cancelled', () => {
                        const accessType = invite.access === 'co-owner' ? 'co-owner' : 'permanent viewer';
                        return { type: accessType  };
                    });

                    await community.deleteInvite(invite.token);
                }}
                canDelete={invite.links.self}
                isNewlyAdded={community.isNewlyAdded(invite)}
            />
        );
    }
    else if (permission){
        return (
            <CollaboratorRow
                id={permission.invite.token}
                data-type={permission.access}
                email={permission.invite.acceptedBy.email}
                name={permission.invite.intendedRecipient.name}
                canDelete={principal.userId !== permission.userId}
                type="permission"
                createdAt={permission.consentedAt}
                onDelete={async () => {
                    if (permission.access === 'co-owner'){
                        trackingService.track('Co-Owner Removed');
                    }
                    else {
                        trackingService.track('Permanent Viewer Removed');
                    }

                    // TODO: token is surprising (expected userId)
                    // but not worth addressing at this time
                    await community.deleteCollaborator(permission.invite.token);
                }}
            />
        );
    }
    else if (owner){
        return (
            <CollaboratorRow
                id="owner"

                // deprecated (new driver doesn't use this)
                data-type="owner"

                role="Owner"
                email={owner.email || ''}
                name={owner.name}
                canDelete={false}
            />
        );
    }
    else {
        throw new Error('one of invite, permission, owner is required');
    }

};
